import { useEffect, useState } from "react";
import { jobService } from "../../../../services/job-service";
import { AdminPageHeader, AdminPanel, EmptyState } from "./admin-ui";

function AdminCategoriesPage() {
    const [categories, setCategories] = useState([]);
    const [message, setMessage] = useState("");
    const [errorMsg, setErrorMsg] = useState("");
    
    // Form state
    const [name, setName] = useState("");
    const [parentId, setParentId] = useState("");
    const [description, setDescription] = useState("");

    const loadData = async () => {
        try {
            const data = await jobService.adminJobCategories();
            setCategories(data.categories || []);
        } catch (err) {
            setErrorMsg(err.message || "Failed to load categories.");
        }
    };

    useEffect(() => {
        loadData();
    }, []);

    const handleCreateCategory = async (e) => {
        e.preventDefault();
        if (!name.trim()) return;
        try {
            await jobService.adminCreateJobCategory({
                name,
                parent_id: parentId || null,
                description
            });
            setMessage("Category created successfully!");
            setName("");
            setParentId("");
            setDescription("");
            loadData();
        } catch (err) {
            setErrorMsg(err.message || "Failed to create category.");
        }
    };

    const handleDeleteCategory = async (id) => {
        if (!window.confirm("Are you sure you want to delete this category?")) return;
        try {
            await jobService.adminDeleteJobCategory(id);
            setMessage("Category deleted successfully.");
            loadData();
        } catch (err) {
            setErrorMsg(err.message || "Failed to delete category.");
        }
    };

    // Filter main categories for the dropdown selection
    const mainCategories = categories.filter(c => !c.parent_id);

    return (
        <>
            <AdminPageHeader title="Job Categories" subtitle="Manage main categories and subcategories" />

            {message && <div className="alert alert-success alert-dismissible fade show" role="alert">
                {message}
                <button type="button" className="btn-close" onClick={() => setMessage("")}></button>
            </div>}
            
            {errorMsg && <div className="alert alert-danger alert-dismissible fade show" role="alert">
                {errorMsg}
                <button type="button" className="btn-close" onClick={() => setErrorMsg("")}></button>
            </div>}

            <div className="row">
                <div className="col-md-4">
                    <AdminPanel title="Add New Category" icon="fa fa-folder-plus">
                        <form onSubmit={handleCreateCategory}>
                            <div className="mb-3">
                                <label className="form-label fw-semibold">Category Name *</label>
                                <input 
                                    type="text" 
                                    className="form-control" 
                                    placeholder="e.g., Software Development" 
                                    value={name} 
                                    onChange={(e) => setName(e.target.value)} 
                                    required 
                                />
                            </div>
                            <div className="mb-3">
                                <label className="form-label fw-semibold">Parent Category</label>
                                <select 
                                    className="form-select" 
                                    value={parentId} 
                                    onChange={(e) => setParentId(e.target.value)}
                                >
                                    <option value="">None (Make Main Category)</option>
                                    {mainCategories.map(c => (
                                        <option key={c.id} value={c.id}>{c.name}</option>
                                    ))}
                                </select>
                                <small className="text-muted d-block mt-1">Select a parent category to make this a subcategory.</small>
                            </div>
                            <div className="mb-3">
                                <label className="form-label fw-semibold">Description</label>
                                <textarea 
                                    className="form-control" 
                                    rows="3" 
                                    placeholder="Brief description..." 
                                    value={description} 
                                    onChange={(e) => setDescription(e.target.value)} 
                                />
                            </div>
                            <button type="submit" className="btn btn-primary w-100">Add Category</button>
                        </form>
                    </AdminPanel>
                </div>
                
                <div className="col-md-8">
                    <AdminPanel title="Categories & Subcategories" icon="fa fa-list">
                        {categories.length === 0 ? (
                            <EmptyState text="No categories available." />
                        ) : (
                            <div className="table-responsive">
                                <table className="table table-hover align-middle">
                                    <thead>
                                        <tr>
                                            <th>Name</th>
                                            <th>Type</th>
                                            <th>Parent Category</th>
                                            <th>Slug</th>
                                            <th className="text-end">Action</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {categories.map(cat => (
                                            <tr key={cat.id}>
                                                <td className="fw-semibold">
                                                    {cat.parent_id ? (
                                                        <span className="ms-3 text-secondary">|-- {cat.name}</span>
                                                    ) : (
                                                        <strong>{cat.name}</strong>
                                                    )}
                                                </td>
                                                <td>
                                                    {cat.parent_id ? (
                                                        <span className="badge bg-light text-dark border">Subcategory</span>
                                                    ) : (
                                                        <span className="badge bg-primary text-white">Main Category</span>
                                                    )}
                                                </td>
                                                <td className="text-muted">
                                                    {cat.parent_name || "-"}
                                                </td>
                                                <td><code>{cat.slug}</code></td>
                                                <td className="text-end">
                                                    <button className="btn btn-outline-danger btn-sm" onClick={() => handleDeleteCategory(cat.id)}>
                                                        <i className="fa fa-trash" />
                                                    </button>
                                                </td>
                                            </tr>
                                        ))}
                                    </tbody>
                                </table>
                            </div>
                        )}
                    </AdminPanel>
                </div>
            </div>
        </>
    );
}

export default AdminCategoriesPage;
